import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ForumTopic {
  title: string;
  author: string;
  authorImage: string;
  authorAlt: string;
  category: string;
  replies: number;
  views: number;
  lastActivity: string;
  solved: boolean;
}

interface Resource {
  icon: string;
  title: string;
  description: string;
  link: string;
}

export default function DeveloperCommunity() {
  const forumTopics: ForumTopic[] = [
  {
    title: "Best practices for handling PNR timeout scenarios",
    author: "Sarah Chen",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_10d60e496-1763295319842.png",
    authorAlt: "Professional headshot of Asian woman with long black hair in business attire",
    category: "PNR Management",
    replies: 24,
    views: 1203,
    lastActivity: "2 hours ago",
    solved: true
  },
  {
    title: "Webhook integration for real-time booking updates",
    author: "Michael Rodriguez",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ddb9b8cc-1763295110271.png",
    authorAlt: "Hispanic man with short dark hair wearing blue shirt smiling at camera",
    category: "API Integration",
    replies: 18,
    views: 856,
    lastActivity: "5 hours ago",
    solved: false
  },
  {
    title: "Payment gateway error codes and handling",
    author: "Emma Thompson",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_13bdacd03-1763298932646.png",
    authorAlt: "Caucasian woman with blonde hair in professional blazer",
    category: "Payments",
    replies: 31,
    views: 1547,
    lastActivity: "1 day ago",
    solved: true
  },
  {
    title: "Optimizing API response times for high-traffic periods",
    author: "David Kim",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_162045a5d-1763295621329.png",
    authorAlt: "Asian man with glasses wearing casual shirt in office setting",
    category: "Performance",
    replies: 42,
    views: 2134,
    lastActivity: "3 days ago",
    solved: true
  }];


  const resources: Resource[] = [
  {
    icon: "BookOpenIcon",
    title: "API Documentation",
    description: "Complete reference for all endpoints and methods",
    link: "#"
  },
  {
    icon: "CodeBracketIcon",
    title: "Code Examples",
    description: "Sample implementations in multiple languages",
    link: "#"
  },
  {
    icon: "VideoCameraIcon",
    title: "Video Tutorials",
    description: "Step-by-step integration guides",
    link: "#"
  },
  {
    icon: "AcademicCapIcon",
    title: "Certification Program",
    description: "Become a certified Dialforflight developer",
    link: "#"
  }];


  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="UserGroupIcon" size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">Developer Community</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Join 5,000+ Developers Building with Dialforflight
          </h2>
          <p className="text-lg text-muted-foreground">
            Connect with fellow developers, share knowledge, and get help from our community and technical team.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Forum Topics */}
          <div className="lg:col-span-2 space-y-6">
            {/* Forum Header */}
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-bold text-foreground">
                Recent Discussions
              </h3>
              <button className="inline-flex items-center space-x-2 text-primary hover:text-secondary font-medium text-sm transition-colors duration-200">
                <Icon name="PlusCircleIcon" size={20} />
                <span>New Topic</span>
              </button>
            </div>

            {/* Forum Topics List */}
            <div className="space-y-4">
              {forumTopics.map((topic, index) =>
              <div
                key={index}
                className="bg-card rounded-xl shadow-brand hover:shadow-brand-lg transition-all duration-300 p-6">

                  <div className="flex items-start space-x-4">
                    {/* Author Avatar */}
                    <div className="flex-shrink-0">
                      <div className="relative w-12 h-12 rounded-full overflow-hidden">
                        <AppImage
                        src={topic.authorImage}
                        alt={topic.authorAlt}
                        className="w-full h-full object-cover" />

                      </div>
                    </div>

                    {/* Topic Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-heading font-semibold text-foreground mb-1 hover:text-primary cursor-pointer transition-colors duration-200">
                            {topic.title}
                          </h4>
                          <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                            <span>{topic.author}</span>
                            <span>•</span>
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                              <Icon name="TagIcon" size={12} />
                              <span>{topic.category}</span>
                            </span>
                          </div>
                        </div>
                        {topic.solved &&
                      <div className="flex-shrink-0 ml-4">
                            <span className="inline-flex items-center space-x-1 px-2 py-1 bg-success/10 text-success rounded-full text-xs font-medium">
                              <Icon name="CheckCircleIcon" size={14} />
                              <span>Solved</span>
                            </span>
                          </div>
                      }
                      </div>

                      {/* Topic Stats */}
                      <div className="flex items-center space-x-6 text-sm text-muted-foreground mt-3">
                        <div className="flex items-center space-x-1">
                          <Icon name="ChatBubbleLeftIcon" size={16} />
                          <span>{topic.replies} replies</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="EyeIcon" size={16} />
                          <span>{topic.views.toLocaleString()} views</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="ClockIcon" size={16} />
                          <span>{topic.lastActivity}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* View All Button */}
            <div className="text-center pt-4">
              <button className="inline-flex items-center space-x-2 text-primary hover:text-secondary font-medium transition-colors duration-200">
                <span>View All Discussions</span>
                <Icon name="ArrowRightIcon" size={18} />
              </button>
            </div>
          </div>

          {/* Right Column - Resources & Stats */}
          <div className="space-y-6">
            {/* Community Stats */}
            <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-6 text-white">
              <h3 className="font-heading text-lg font-semibold mb-4">
                Community Stats
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">Active Developers</span>
                  <span className="font-heading text-xl font-bold">5,247</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">Topics Discussed</span>
                  <span className="font-heading text-xl font-bold">12,893</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">Solutions Posted</span>
                  <span className="font-heading text-xl font-bold">8,456</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">Avg Response Time</span>
                  <span className="font-heading text-xl font-bold">&lt;2h</span>
                </div>
              </div>
            </div>

            {/* Developer Resources */}
            <div className="bg-card rounded-xl shadow-brand p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                Developer Resources
              </h3>
              <div className="space-y-3">
                {resources.map((resource, index) =>
                <button
                  key={index}
                  className="w-full flex items-start space-x-3 p-3 rounded-lg hover:bg-surface transition-colors duration-200 text-left">

                    <Icon name={resource.icon as any} size={24} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-heading font-semibold text-foreground text-sm mb-0.5">
                        {resource.title}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {resource.description}
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Support CTA */}
            <div className="bg-card rounded-xl shadow-brand p-6">
              <Icon name="LifebuoyIcon" size={32} className="text-primary mb-3" />
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                Need Direct Support?
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our technical team is available 24/7 to help with integration challenges.
              </p>
              <button className="w-full bg-primary hover:bg-secondary text-white py-2.5 rounded-lg font-heading font-semibold text-sm transition-colors duration-200">
                Contact Support Team
              </button>
            </div>

            {/* Newsletter Signup */}
            <div className="bg-surface rounded-xl p-6">
              <Icon name="EnvelopeIcon" size={32} className="text-primary mb-3" />
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                Developer Newsletter
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get weekly updates on new features, best practices, and community highlights.
              </p>
              <div className="flex space-x-2">
                <input
                  type="email"
                  placeholder="your@email.com"
                  className="flex-1 px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

                <button className="px-4 py-2 bg-primary hover:bg-secondary text-white rounded-lg font-medium text-sm transition-colors duration-200">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}